import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaVideo, FaExclamationTriangle, FaRedo, FaExpand, FaCompress } from 'react-icons/fa';
import { getMediaUrl, formatTime, getOptimizedImageUrl, getOptimizedVideoUrl } from '../../utils/mediaUtils';

/**
 * Component for displaying a gallery of videos
 */
const VideoGallery = ({ 
  videoItems, 
  visibleCount,
  loading,
  isPlaying, 
  currentTimes, 
  durations, 
  isMuted,
  playbackErrors,
  videoRefs,
  progressRefs,
  toggleVideo,
  toggleMute,
  handleTimeUpdate,
  handleMediaEnded,
  handleMetadataLoaded,
  handleSeek,
  clearPlaybackError,
  openLightbox
}) => {
  // Track video loading errors
  const [videoErrors, setVideoErrors] = useState({});
  // Track videos with warnings (might play but with issues)
  const [videoWarnings, setVideoWarnings] = useState({});
  // Track video loading states
  const [videoLoading, setVideoLoading] = useState({});
  // Track loaded thumbnails
  const [loadedThumbnails, setLoadedThumbnails] = useState({});
  // Track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState({});
  // Detect iOS platform
  const [isIOS, setIsIOS] = useState(false);
  // Ref for the gallery
  const galleryRef = useRef(null);
  // Ref to store loading timeouts
  const loadingTimeoutsRef = useRef({});
  // Ref for video containers
  const videoContainerRefs = useRef({});
  // Add volume state
  const [volumes, setVolumes] = useState({});

  // Handle touch events for iOS video controls
  const handleTouchStart = useCallback((id) => {
    const container = videoContainerRefs.current[id];
    if (isIOS && container) {
      container.classList.add('touching');
      container.classList.add('controls-visible');
    }
  }, [isIOS]);

  const handleTouchEnd = useCallback((id) => {
    const container = videoContainerRefs.current[id];
    if (isIOS && container) {
      container.classList.remove('touching');
      // Keep controls visible for 3 seconds after touch ends
      setTimeout(() => {
        if (container) {
          container.classList.remove('controls-visible');
        }
      }, 3000);
    }
  }, [isIOS]);

  // Handle control interaction to keep controls visible
  const handleControlInteraction = useCallback((id) => {
    const container = videoContainerRefs.current[id];
    if (isIOS && container) {
      container.classList.add('controls-visible');
      // Reset the timeout when interacting with controls
      clearTimeout(container.controlsTimeout);
      container.controlsTimeout = setTimeout(() => {
        if (container) {
          container.classList.remove('controls-visible');
        }
      }, 3000);
    }
  }, [isIOS]);

  // Detect iOS platform
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  // Combine playback errors with loading errors
  useEffect(() => {
    // If we have new playback errors, add them to our error state
    if (playbackErrors) {
      Object.entries(playbackErrors).forEach(([id, error]) => {
        if (error) {
          // Only mark as error if it's a critical error
          if (error.includes('MEDIA_ERR_SRC_NOT_SUPPORTED') || 
              error.includes('MEDIA_ERR_DECODE') ||
              error.includes('Failed to fetch')) {
            setVideoErrors(prev => ({ ...prev, [id]: true }));
          } else {
            // For other errors, just show a warning
            setVideoWarnings(prev => ({ ...prev, [id]: true }));
          }
        }
      });
    }
  }, [playbackErrors]);

  // Force reload of any videos that are stuck loading after 8 seconds
  useEffect(() => {
    const stuckVideosTimeout = setTimeout(() => {
      if (!videoItems || videoItems.length === 0) return;
      
      // Find videos that are still in loading state
      const stuckVideos = videoItems.slice(0, visibleCount).filter(item => 
        videoLoading[item._id] === true && !videoErrors[item._id]
      );
      
      // Retry loading stuck videos
      if (stuckVideos.length > 0) {
        stuckVideos.forEach(item => {
          handleRetry(item._id);
        });
      }
    }, 8000);
    
    return () => clearTimeout(stuckVideosTimeout);
  }, [videoItems, visibleCount, videoLoading, videoErrors]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      
      // If exiting fullscreen, update all videos' fullscreen state
      if (!isCurrentlyFullscreen) {
        setIsFullscreen({});
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Cleanup function for loading timeouts
  const cleanupLoadingTimeout = (id) => {
    if (loadingTimeoutsRef.current[id]) {
      clearTimeout(loadingTimeoutsRef.current[id]);
      delete loadingTimeoutsRef.current[id];
    }
  };

  // Cleanup timeouts when component unmounts
  useEffect(() => {
    return () => {
      Object.keys(loadingTimeoutsRef.current).forEach(id => {
        cleanupLoadingTimeout(id);
      });
    };
  }, []);

  // Handle volume change
  const handleVolumeChange = useCallback((id, value) => {
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;
    
    // Update volume state
    setVolumes(prev => ({ ...prev, [id]: value }));
    
    // Set video volume
    videoElement.volume = value;
    
    // Update mute state based on volume
    if (value === 0 && !isMuted[id]) {
      toggleMute(id, 'video');
    } else if (value > 0 && isMuted[id]) {
      toggleMute(id, 'video');
    }
  }, [videoRefs, isMuted, toggleMute]);

  // Handle mute toggle with volume sync
  const handleMuteToggle = useCallback((id) => {
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;
    
    if (isMuted[id]) {
      // Unmute and restore previous volume or default to 1
      const previousVolume = volumes[id] || 1;
      videoElement.volume = previousVolume;
      setVolumes(prev => ({ ...prev, [id]: previousVolume }));
    } else {
      // Store current volume before muting
      const currentVolume = videoElement.volume;
      setVolumes(prev => ({ ...prev, [id]: currentVolume }));
      videoElement.volume = 0;
    }
    
    toggleMute(id, 'video');
  }, [videoRefs, isMuted, volumes, toggleMute]);

  // Handle video metadata loaded
  const handleVideoMetadataLoaded = useCallback((id, videoElement) => {
    if (!videoElement) return;
    
    // For iOS devices, ensure proper audio state initialization
    if (isIOS) {
      const shouldMute = isMuted[id] === true;
      videoElement.muted = shouldMute;

      videoElement.addEventListener('loadedmetadata', () => {
        videoElement.muted = isMuted[id] === true;
        videoElement.volume = shouldMute ? 0 : (volumes[id] || 1);
      }, { once: true });
    }
    
    handleMetadataLoaded(id, videoElement);
  }, [isIOS, isMuted, volumes, handleMetadataLoaded]);

  if (videoItems.length === 0) {
    return (
      <div className="empty-gallery">
        <p>No videos found in the gallery.</p>
      </div>
    );
  }
  
  // Only render visible videos for better performance
  const visibleVideos = videoItems.slice(0, visibleCount);
  
  // Handle video error
  const handleVideoError = (id, error) => {
    // Check if the error is a network error or format error
    const errorEvent = error?.nativeEvent;
    
    if (errorEvent) {
      console.warn(`Video error for ID ${id}:`, errorEvent);
      
      // Only mark as error if it's a critical error
      if (errorEvent.type === 'error' && videoRefs.current[id]) {
        const videoElement = videoRefs.current[id];
        const errorCode = videoElement.error ? videoElement.error.code : null;
        
        // MEDIA_ERR_SRC_NOT_SUPPORTED (4) or MEDIA_ERR_DECODE (3) are critical
        if (errorCode === 4 || errorCode === 3) {
          setVideoErrors(prev => ({ ...prev, [id]: true }));
        } else {
          // For other errors, just show a warning
          setVideoWarnings(prev => ({ ...prev, [id]: true }));
        }
      } else {
        // If we can't determine the error type, show a warning
        setVideoWarnings(prev => ({ ...prev, [id]: true }));
      }
    } else {
      // If we can't determine the error type, show a warning
      setVideoWarnings(prev => ({ ...prev, [id]: true }));
    }
    
    // Clear loading state
    setVideoLoading(prev => ({ ...prev, [id]: false }));
  };

  // Handle video loading start
  const handleVideoLoadStart = (id) => {
    setVideoLoading(prev => ({ ...prev, [id]: true }));
    
    // Set a timeout to mark the video as error if it takes too long
    cleanupLoadingTimeout(id);
    
    // Use a shorter timeout for iOS devices as they handle video loading differently
    const timeoutDuration = isIOS ? 6000 : 15000;
    
    loadingTimeoutsRef.current[id] = setTimeout(() => {
      console.warn(`Video load timed out: ${id}`);
      setVideoLoading(prev => {
        // Only mark as not loading if it's still loading
        if (prev[id]) {
          return { ...prev, [id]: false };
        }
        return prev;
      });
      
      // On iOS, don't mark as error immediately as videos might still load
      if (!isIOS) {
        setVideoErrors(prev => ({ ...prev, [id]: true }));
      }
    }, timeoutDuration);
  };

  // Handle video loaded
  const handleVideoLoaded = (id) => {
    setVideoLoading(prev => ({ ...prev, [id]: false }));
    cleanupLoadingTimeout(id);
  };

  // For iOS devices, we need additional handler as canplay sometimes doesn't fire
  const handleVideoLoadedData = (id) => {
    if (isIOS) {
      setVideoLoading(prev => ({ ...prev, [id]: false }));
      cleanupLoadingTimeout(id);
    }
  };

  // Handle suspicious state
  const handleVideoSuspend = (id) => {
    // On iOS, suspend often means the video is ready but paused
    if (isIOS && videoRefs.current[id] && videoRefs.current[id].readyState >= 2) {
      setVideoLoading(prev => ({ ...prev, [id]: false }));
      cleanupLoadingTimeout(id);
    }
  };

  // Handle thumbnail loaded
  const handleThumbnailLoad = (id) => {
    setLoadedThumbnails(prev => ({ ...prev, [id]: true }));
    
    // On iOS, if thumbnails load but video is still loading after a delay, 
    // consider the video ready
    if (isIOS) {
      setTimeout(() => {
        setVideoLoading(prev => {
          if (prev[id]) {
            return { ...prev, [id]: false };
          }
          return prev;
        });
      }, 1000);
    }
  };

  // Handle thumbnail error
  const handleThumbnailError = (id) => {
    setLoadedThumbnails(prev => ({ ...prev, [id]: 'error' }));
  };

  // Handle retry for a video that had an error
  const handleRetry = (id) => {
    setVideoErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
    setVideoWarnings(prev => {
      const newWarnings = { ...prev };
      delete newWarnings[id];
      return newWarnings;
    });
    setVideoLoading(prev => ({ ...prev, [id]: true }));
    clearPlaybackError(id);
    
    // Force reload by recreating the video element
    if (videoRefs.current[id]) {
      const videoElement = videoRefs.current[id];
      const src = videoElement.src;
      videoElement.src = '';
      setTimeout(() => {
        if (videoRefs.current[id]) {
          videoRefs.current[id].src = src;
          videoRefs.current[id].load();
        }
      }, 100);
    }
  };

  // Toggle fullscreen for a video
  const toggleFullscreen = (id, e) => {
    e.stopPropagation(); // Prevent triggering play/pause
    
    const container = videoContainerRefs.current[id];
    const videoElement = videoRefs.current[id];
    
    if (!container || !videoElement) return;
    
    if (!isFullscreen[id]) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen().then(() => {
          setIsFullscreen(prev => ({ ...prev, [id]: true }));
          
          // Ensure video fills the screen in fullscreen mode
          videoElement.style.maxHeight = '100vh';
          videoElement.style.height = '100%';
          videoElement.style.objectFit = 'contain';
          
          // Auto-play when entering fullscreen
          if (!isPlaying[id]) {
            toggleVideo(id);
          }
        }).catch(err => {
          console.error('Error attempting to enable fullscreen:', err);
        });
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
        setIsFullscreen(prev => ({ ...prev, [id]: true }));
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
        setIsFullscreen(prev => ({ ...prev, [id]: true }));
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
        setIsFullscreen(prev => ({ ...prev, [id]: true }));
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      
      setIsFullscreen(prev => ({ ...prev, [id]: false }));
      
      // Reset video styles
      videoElement.style.maxHeight = '';
      videoElement.style.height = '';
      videoElement.style.objectFit = '';
    }
  };

  // Open video in lightbox
  const handleOpenLightbox = (item, index, e) => {
    e.stopPropagation(); // Prevent triggering play/pause
    
    // Pause the video if it's playing
    if (isPlaying[item._id]) {
      toggleVideo(item._id);
    }
    
    // Open the lightbox with the FULL video list so prev/next navigation isn't
    // limited to the videos currently rendered (visibleVideos is the same order,
    // so the index still lines up).
    openLightbox(item, index, videoItems);
  };
  
  // Handle video click with proper iOS audio handling
  const handleVideoClick = (id) => {
    if (videoErrors[id]) return;
    
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;
    
    // If we're on iOS, we need to ensure proper state resetting
    if (isIOS) {
      // Reset any automatic playing that might be happening
      if (!isPlaying[id]) {
        videoElement.pause();
        videoElement.currentTime = 0;
        // Ensure audio state is properly set
        videoElement.muted = isMuted[id] === true;
        if (!videoElement.muted) {
          videoElement.volume = volumes[id] || 1;
        }
      }
    }
    
    // Toggle the video play state
    toggleVideo(id);
  };

  return (
    <div className="video-gallery" ref={galleryRef}>
      {visibleVideos.map((item, index) => {
        const mediaUrl = getMediaUrl(item.filePath);
        const videoUrls = isIOS ? getOptimizedVideoUrl(item.filePath, item.fileType, true) : { original: mediaUrl };
        const hasError = videoErrors[item._id];
        const hasWarning = videoWarnings[item._id];
        const isLoading = videoLoading[item._id];
        // Use the custom thumbnail if available, otherwise fall back to the video's first frame
        const thumbnailUrl = item.thumbnailPath ? getOptimizedImageUrl(item.thumbnailPath, 'thumbnail') : undefined;
        const isThumbnailLoaded = loadedThumbnails[item._id];
        const hasThumbnailError = isThumbnailLoaded === 'error';
        const isVideoFullscreen = isFullscreen[item._id];
        
        return (
          <div 
            className="video-item" 
            key={item._id}
            style={{ 
              animationDelay: `${0.1 + (index * 0.05)}s` 
            }}
          >
            <div 
              className={`video-container ${isVideoFullscreen ? 'video-fullscreen' : ''} ${isLoading ? 'loading' : ''}`}
              ref={el => videoContainerRefs.current[item._id] = el}
              onTouchStart={() => handleTouchStart(item._id)}
              onTouchEnd={() => handleTouchEnd(item._id)}
            >
              {!hasError ? (
                <>
                  <video 
                    ref={el => videoRefs.current[item._id] = el}
                    preload={isIOS ? "auto" : "metadata"}
                    poster={thumbnailUrl}
                    onClick={() => handleVideoClick(item._id)}
                    onError={(e) => handleVideoError(item._id, e)}
                    onTimeUpdate={() => handleTimeUpdate(item._id, videoRefs.current[item._id])}
                    onEnded={() => handleMediaEnded(item._id)}
                    onLoadedMetadata={() => handleVideoMetadataLoaded(item._id, videoRefs.current[item._id])}
                    onLoadStart={() => handleVideoLoadStart(item._id)}
                    onCanPlay={() => handleVideoLoaded(item._id)}
                    onLoadedData={() => handleVideoLoadedData(item._id)}
                    onSuspend={() => handleVideoSuspend(item._id)}
                    onWaiting={() => setVideoLoading(prev => ({ ...prev, [item._id]: true }))}
                    playsInline
                    muted={isMuted[item._id] === true}
                    autoPlay={false}
                    loop={false}
                    className={isVideoFullscreen ? 'video-fullscreen-active' : ''}
                  >
                    <source src={isIOS ? videoUrls.primary : mediaUrl} type={isIOS ? "video/mp4" : item.fileType} />
                    {item.fileType === 'video/quicktime' && (
                      <>
                        <source src={`${mediaUrl}?format=mp4`} type="video/mp4" />
                        <source src={`${mediaUrl}?format=webm`} type="video/webm" />
                      </>
                    )}
                    {isIOS && (
                      <>
                        <source src={videoUrls.mp4} type="video/mp4" />
                        <source src={videoUrls.webm} type="video/webm" />
                      </>
                    )}
                    Your browser does not support the video tag or this video format.
                  </video>
                  
                  {/* Thumbnail preloader (hidden) */}
                  {thumbnailUrl && (
                    <img 
                      src={thumbnailUrl}
                      alt=""
                      style={{ display: 'none' }}
                      onLoad={() => handleThumbnailLoad(item._id)}
                      onError={() => handleThumbnailError(item._id)}
                    />
                  )}
                  
                  {/* Loading overlay */}
                  {isLoading && (
                    <div className="video-loading-overlay">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  
                  {!isPlaying[item._id] && !hasError && !isLoading && (
                    <div className="video-overlay">
                      <button 
                        className="video-play-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoClick(item._id);
                        }}
                        aria-label="Play"
                        type="button"
                      >
                        <FaPlay />
                      </button>
                      {hasWarning && (
                        <div className="video-warning-badge">
                          <FaExclamationTriangle />
                        </div>
                      )}
                    </div>
                  )}
                  <div 
                    className="video-controls" 
                    onClick={e => {
                      e.stopPropagation();
                      handleControlInteraction(item._id);
                    }}
                  >
                    <div className="video-controls-primary">
                      <button 
                        className="video-play-button-small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoClick(item._id);
                        }}
                        aria-label={isPlaying[item._id] ? "Pause" : "Play"}
                        type="button"
                        disabled={hasError}
                      >
                        {isPlaying[item._id] ? <FaPause /> : <FaPlay />}
                      </button>
                      <div className="video-time">
                        <span className="current-time">{formatTime(currentTimes[item._id] || 0)}</span>
                        <span className="duration">{formatTime(durations[item._id] || 0)}</span>
                      </div>
                    </div>
                    <div 
                      className="video-progress-container"
                      onClick={(e) => !hasError && handleSeek(e, item._id, 'video')}
                    >
                      <div className="video-progress-bg">
                        <div 
                          className="video-progress-bar" 
                          ref={el => progressRefs.current[item._id] = el}
                        ></div>
                      </div>
                    </div>
                    <div className="video-controls-secondary">
                      <button 
                        className="video-mute-button"
                        onClick={() => handleMuteToggle(item._id)}
                        aria-label={isMuted[item._id] ? "Unmute" : "Mute"}
                        type="button"
                        disabled={hasError}
                      >
                        {isMuted[item._id] ? <FaVolumeMute /> : <FaVolumeUp />}
                      </button>
                      
                      {/* Add volume slider */}
                      <div className="video-volume-control">
                        <div className="video-volume-slider-wrapper">
                          <div 
                            className="video-volume-level-bg" 
                            style={{ width: `${isMuted[item._id] ? 0 : (volumes[item._id] || 1) * 100}%` }}
                          />
                          <input
                            type="range"
                            className="video-volume-slider"
                            min="0"
                            max="1"
                            step="0.01"
                            value={isMuted[item._id] ? 0 : (volumes[item._id] || 1)}
                            onChange={(e) => handleVolumeChange(item._id, parseFloat(e.target.value))}
                            aria-label="Volume"
                          />
                        </div>
                      </div>
                      
                      <button
                        className="video-fullscreen-button"
                        onClick={(e) => handleOpenLightbox(item, index, e)}
                        aria-label="Open in fullscreen"
                        type="button"
                        disabled={hasError}
                      >
                        <FaExpand />
                      </button>
                      {hasWarning && (
                        <div className="video-warning-text">
                          <FaExclamationTriangle /> Limited compatibility
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                // Display error state for videos that failed to load
                <div className="video-error-container">
                  <div className="video-error-overlay">
                    <FaVideo />
                    <span>Video unavailable</span>
                    <p>This video format may not be supported by your browser or the file may be missing.</p>
                    <button 
                      className="video-retry-button"
                      onClick={() => handleRetry(item._id)}
                      aria-label="Retry loading video"
                    >
                      <FaRedo /> Retry
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="video-info">
              <h3>{item.title}</h3>
              {item.description && <p>{item.description}</p>}
            </div>
          </div>
        );
      })}
      
      {/* Show loading indicator when loading more videos */}
      {loading && visibleCount > 20 && (
        <div className="gallery-loading-more">
          <div className="loading-spinner"></div>
          <p>Loading more videos...</p>
        </div>
      )}
    </div>
  );
};

export default VideoGallery; 